import React, { useEffect, useState } from "react";
import { ArrowClockwise, PencilSimple, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

import { mail as mailApi } from "@/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ActaConfirmDialog from "@/components/ActaConfirmDialog";
import MailScheduledEditDialog from "@/components/mail/MailScheduledEditDialog";


const STATUS_LABELS = {
  preparing: "Préparation",
  pending: "Planifié",
  sending: "Envoi en cours",
  sent: "Envoyé",
  failed: "Échec de l’envoi",
  cancelled: "Annulé",
};


function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}


export default function MailScheduledList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [editing, setEditing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  async function openEdit(item) {
    if (actionBusy || item.status !== "pending") return;
    setActionBusy(true);
    try {
      const latest = await mailApi.getScheduled(item.id);
      if (latest.status !== "pending") {
        toast.error("Cette programmation n’est plus modifiable.");
        setRefresh((value) => value + 1);
        return;
      }
      setEditing(latest);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Lecture de la programmation impossible");
    } finally {
      setActionBusy(false);
    }
  }

  async function confirmCancel() {
    if (!cancelTarget || actionBusy) return;
    setActionBusy(true);
    try {
      await mailApi.cancelScheduled(cancelTarget.id, cancelTarget.revision);
      toast.success("Envoi planifié annulé");
      setCancelTarget(null);
      setRefresh((value) => value + 1);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Annulation impossible");
      if (error?.response?.status === 409) {
        setCancelTarget(null);
        setRefresh((value) => value + 1);
      }
    } finally {
      setActionBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    mailApi.listScheduled()
      .then((result) => {
        if (!cancelled) setItems(result?.items || []);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.response?.data?.detail || "Chargement des mails planifiés impossible");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <>
    <Card variant="acta" className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden" data-testid="mail-scheduled-list">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--do-border)] p-4">
        <div>
          <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
            Envois planifiés
          </h2>
          <p className="text-xs text-[var(--do-muted)]">
            Date prévue et état du traitement des messages programmés.
          </p>
        </div>
        <Button
          type="button"
          variant="actaSecondary"
          onClick={() => setRefresh((value) => value + 1)}
          disabled={loading}
          data-testid="mail-scheduled-refresh"
        >
          <ArrowClockwise size={17} />
          Actualiser
        </Button>
      </div>
      <div className="min-h-0 overflow-y-auto">
        {loading && <p className="p-4 text-sm text-[var(--do-muted)]">Chargement…</p>}
        {!loading && !items.length && (
          <p className="p-6 text-center text-sm text-[var(--do-muted)]">
            Aucun envoi planifié.
          </p>
        )}
        {!loading && items.map((item) => (
          <article
            key={item.id}
            className="flex flex-wrap justify-between gap-3 border-b border-[var(--do-border)] p-4"
            data-testid="mail-scheduled-row"
          >
            <div className="min-w-0 flex-1">
              <div className="break-words text-sm font-bold text-[var(--do-ink)]">
                {item.subject || "(sans objet)"}
              </div>
              <div className="mt-1 break-words text-xs text-[var(--do-muted)]">
                À : {(item.to || []).join(", ")}
              </div>
              <div className="mt-2 whitespace-pre-wrap break-words text-xs text-[var(--do-ink)]">
                {item.text || "(contenu manquant)"}
              </div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                Prévu : {formatDate(item.scheduledAt)}
                {item.attachmentCount > 0
                  ? ` · ${item.attachmentCount} pièce(s) jointe(s)`
                  : ""}
              </div>
              {(item.attachmentNames || []).length > 0 && (
                <div className="mt-1 break-words text-xs text-[var(--do-muted)]">
                  Pièces jointes : {item.attachmentNames.join(", ")}
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <span className="text-xs font-bold text-[var(--do-ink)]" role="status">
                {STATUS_LABELS[item.status] || item.status}
              </span>
              {item.status === "pending" && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="actaSecondary" size="sm"
                    disabled={actionBusy} onClick={() => openEdit(item)}
                    data-testid="mail-scheduled-edit-action">
                    <PencilSimple size={15} /> Modifier
                  </Button>
                  <Button type="button" variant="actaSecondary" size="sm"
                    disabled={actionBusy} onClick={() => setCancelTarget(item)}
                    data-testid="mail-scheduled-cancel-action">
                    <XCircle size={15} /> Annuler l’envoi
                  </Button>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </Card>
    {editing && (
      <MailScheduledEditDialog
        key={`${editing.id}-${editing.revision}`}
        item={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          setRefresh((value) => value + 1);
        }}
      />
    )}
    <ActaConfirmDialog
      open={Boolean(cancelTarget)}
      title="Annuler l’envoi planifié ?"
      description="Le message ne sera pas envoyé. La programmation annulée restera visible dans l’historique."
      confirmLabel="Annuler l’envoi"
      saving={actionBusy}
      danger
      onClose={() => setCancelTarget(null)}
      onConfirm={confirmCancel}
    />
    </>
  );
}
